import { Component, computed, DestroyRef, inject, OnInit, Signal, signal, viewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, pairwise, startWith, take } from 'rxjs';
import { Router, RouterOutlet } from '@angular/router';
import { CookieService } from 'ng2-cookies';
import { LicenseInfo } from 'app/models/api/license-info';
import { expires } from 'app/utils/expires';
import { generateShortId } from 'app/utils/shortid';
import { ThemeService } from 'app/services/theme/theme.service';
import { LocalizedUrl } from 'app/services/theme/theme.models';
import { LanguageDescription, LanguageService } from 'app/services/language.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ModalComponent } from '../../components/layout/modal/modal.component';
import { DATA_PRIVACY_FALLBACK_URL, IMPRINT_FALLBACK_URL } from '../../standalone-core/tokens/external-urls';
import { LicenseState } from '../../models/api/api-response';
import { ApplicationStore } from 'app/state-management/application/application.store';
import { RouteStore } from 'app/state-management/route/route.store';
import { ProfileStore } from 'app/state-management/profile/profile.store';
import { VersionIntroStore } from 'app/state-management/version-intro/version-intro.store';
import { isAssetOpenInNextToModeSignal } from 'app/state-management/application/application.selectors';
import { FngButtonComponent, FngSnackbarContainerComponent } from '@festo-ui/angular';
import {
  RightContentViewOverlayComponent,
} from 'app/components/layout/right-content-view-overlay/right-content-view-overlay.component';
import { AlertsComponent } from 'app/components/layout/alerts.component';
import { AssetLayoutComponent } from 'app/containers/layout/asset-layout/asset-layout.component';
import { FeedbackComponent } from 'app/components/layout/feedback/feedback.component';
import { FooterComponent } from 'app/components/footer/footer.component';
import { NavigationComponent } from 'app/components/layout/navigation/navigation.component';
import { PrimaryButtonDirective } from 'app/directives/primary-button.directive';
import { CustomSanitizeHtmlPipe } from 'app/pipes/custom-sanitize-html.pipe';
import { getLicenseRedirect, resolveLocalizedUrl } from './layout.utils';

@Component({
  selector: 'sma-web-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['layout.component.scss'],
  imports: [
    RouterOutlet,
    TranslateModule,
    FngButtonComponent,
    FngSnackbarContainerComponent,
    ModalComponent,
    RightContentViewOverlayComponent,
    AlertsComponent,
    AssetLayoutComponent,
    FeedbackComponent,
    FooterComponent,
    NavigationComponent,
    PrimaryButtonDirective,
    CustomSanitizeHtmlPipe,
  ],
})
export class LayoutComponent implements OnInit {
  private readonly destroy: DestroyRef = inject(DestroyRef);
  private readonly profileStore = inject(ProfileStore);
  private readonly versionIntroStore = inject(VersionIntroStore);
  private readonly router: Router = inject(Router);
  private readonly cookieService: CookieService = inject(CookieService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly themeService: ThemeService = inject(ThemeService);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly dataPrivacyFallbackUrl: string = inject(DATA_PRIVACY_FALLBACK_URL);
  private readonly imprintFallbackUrl: string = inject(IMPRINT_FALLBACK_URL);
  private readonly appStore: ApplicationStore = inject(ApplicationStore);
  protected readonly routeStore = inject(RouteStore);

  private readonly accountExpiry: Signal<ModalComponent> = viewChild.required<ModalComponent>('accountExpiry');

  private readonly language: Signal<LanguageDescription | null> = toSignal(this.languageService.langChanged$, {
    initialValue: this.languageService.currentLanguage,
  });
  private readonly tenantImprintUrl: Signal<LocalizedUrl | null> = toSignal(this.themeService.imprintUrl$, {
    initialValue: null,
  });
  private readonly tenantDataprivacyUrl: Signal<LocalizedUrl | null> = toSignal(this.themeService.dataprivacyUrl$, {
    initialValue: null,
  });

  protected readonly alerts = this.appStore.alerts;
  protected readonly isAssetTreeNextTo: Signal<boolean> = isAssetOpenInNextToModeSignal();
  protected readonly appName: Signal<string | null> = toSignal(this.themeService.appName$, { initialValue: null });
  protected readonly contactMailAddress: Signal<string | null> = toSignal(this.themeService.contactMailAddress$, {
    initialValue: null,
  });
  protected readonly imprintUrl: Signal<string> = computed(() =>
    resolveLocalizedUrl(
      this.tenantImprintUrl(),
      this.language()?.languageKey,
      this.language()?.imprintUrl,
      this.imprintFallbackUrl,
    ),
  );
  protected readonly dataprivacyUrl: Signal<string> = computed(() =>
    resolveLocalizedUrl(
      this.tenantDataprivacyUrl(),
      this.language()?.languageKey,
      this.language()?.dataprivacyUrl,
      this.dataPrivacyFallbackUrl,
    ),
  );
  readonly daysToDeleteAccount = signal<number>(0);

  ngOnInit(): void {
    this.profileStore.hasAnyLicenseOrNull$
      .pipe(
        startWith(null),
        pairwise(),
        takeUntilDestroyed(this.destroy),
      )
      .subscribe(([previous, current]: [boolean | null, boolean | null]) => {
        const redirect: string[] | null = getLicenseRedirect(previous, current, this.router.url);
        if (redirect) {
          this.router.navigate(redirect);
        }
      });

    this.profileStore.license$
      .pipe(
        filter((license: LicenseInfo | null): license is LicenseInfo => license !== null),
        take(1),
        takeUntilDestroyed(this.destroy),
      )
      .subscribe((license: LicenseInfo) => this.showLicenseNotices(license));

    this.profileStore.initLayout();
  }

  navigateOnReleaseInformation(): void {
    this.versionIntroStore.navigateToReleaseNotes();
  }

  protected dismissAlert(alertId: string): void {
    this.appStore.dismissAlert(alertId);
  }

  private showLicenseNotices(license: LicenseInfo): void {
    if (license.state === LicenseState.EXPIRED && !license.ignoreExpiredLicenseState) {
      this.appStore.addAlert({
        type: 'warning',
        id: generateShortId(),
        title: this.translateService.instant('MISC.LICENSE_TITLE'),
        message: this.translateService.instant('MISC.LICENSE_MESSAGE', { validTo: license.validTo.toLocaleString() }),
        showLogout: false,
      });
    }
    if (license.isUserLicenseGetsExpired && !this.cookieService.check('sma-web-account-expiry')) {
      this.daysToDeleteAccount.set(license.userLicenseRemainingDays);
      this.accountExpiry().show();
      this.cookieService.set('sma-web-account-expiry', 'account expiry will be hidden', expires(), '/');
    }
  }
}
